import type { ReactElement } from 'react';
import { useEffect, useState, forwardRef } from 'react';
import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Autocomplete,
  ListBox,
  Popover,
  SelectValue,
  type ValidationResult,
  useFilter,
} from 'react-aria-components';
import cx from 'clsx';
import { isNil } from 'es-toolkit';
import { IconChevronBottomOutlined, IconChevronTopOutlined } from '@repo/monochrome-icons';
import { SearchFieldWithDebounce } from '../search-field-with-debounce';
import { SearchField } from '../search-field';
import {
  Description,
  Error,
  Label,
  Option,
  OptionDivider,
  OptionGroup,
  Trigger,
} from '../form-elements';
import styles from './select.module.css';

export interface SelectProps<T extends object> extends AriaSelectProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder: string;
  multiple?: boolean;
  isOptional?: boolean;
  isOptionalText?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  selectedValueClassName?: string;
  displaySelectedTextValue?: boolean;
  withSearch?: {
    async?: boolean;
    placeholder: string;
    emptyMessage?: string;
  };
  autocompleteInputValue?: string;
  autocompleteOnInputChange?: (value: string) => void;
  portalContainerId?: string;
}

const SelectComponent = forwardRef(function SelectComponent<T extends object>(
  {
    label,
    description,
    errorMessage,
    isOptional,
    isOptionalText,
    children,
    defaultOpen = false,
    isOpen,
    onOpenChange,
    isDisabled,
    isReadOnly,
    fullWidth = true,
    selectedValueClassName,
    displaySelectedTextValue,
    withSearch,
    portalContainerId,
    placeholder,
    autocompleteInputValue,
    autocompleteOnInputChange,
    ...props
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
  // eslint-disable-next-line @typescript-eslint/unbound-method -- Not sure why eslint complains here
  const { contains } = useFilter({ sensitivity: 'base' });
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  function handleOpenChange(open: boolean): void {
    setInternalIsOpen(open);
    onOpenChange?.(open);
  }

  useEffect(() => {
    if (!isNil(isOpen)) {
      setInternalIsOpen(isOpen);
    }
  }, [isOpen]);

  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  const autocompleteProps = withSearch?.async
    ? {
        inputValue: autocompleteInputValue,
        onInputChange: autocompleteOnInputChange,
      }
    : {
        filter: contains,
      };

  const SearchComponent = withSearch?.async ? SearchFieldWithDebounce : SearchField;

  return (
    <AriaSelect
      {...props}
      data-readonly={String(Boolean(isReadOnly) && !isDisabled)}
      isDisabled={isDisabled || isReadOnly}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placeholder={placeholder}
      ref={ref}
    >
      <div className={styles.container}>
        {Boolean(label) && (
          <Label>
            {label} {Boolean(isOptional) && isOptionalText}
          </Label>
        )}
        <Trigger className={styles['input-container']} isReadOnly={isReadOnly}>
          <SelectValue className={cx(styles['input-value'], selectedValueClassName)}>
            {displaySelectedTextValue
              ? ({ selectedText, defaultChildren }) => {
                  return selectedText || defaultChildren;
                }
              : undefined}
          </SelectValue>
          {internalIsOpen ? (
            <IconChevronTopOutlined aria-hidden />
          ) : (
            <IconChevronBottomOutlined
              aria-hidden
              className={cx(isReadOnly && styles['chevron-icon--readonly'])}
            />
          )}
        </Trigger>
        <Error>{errorMessage}</Error>
        {Boolean(description) && <Description>{description}</Description>}
      </div>
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        className={cx('body-2', styles.popover, fullWidth && styles['full-width'])}
        offset={8}
        placement="bottom start"
      >
        {withSearch ? (
          <Autocomplete {...autocompleteProps}>
            <SearchComponent inputClassName={styles.search} placeholder={withSearch.placeholder} />
            <ListBox
              className={styles['option-list']}
              renderEmptyState={() =>
                withSearch.emptyMessage ? (
                  <div className={styles['no-results']}>{withSearch.emptyMessage}</div>
                ) : null
              }
            >
              {children}
            </ListBox>
          </Autocomplete>
        ) : (
          <ListBox className={styles['option-list']}>{children}</ListBox>
        )}
      </Popover>
    </AriaSelect>
  );
}) as <T extends object>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReactElement;

export const Select = Object.assign(SelectComponent, {
  Option,
  Divider: OptionDivider,
  Group: OptionGroup,
});
